import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CloseIconButton2, Modal, PageLoading } from "@tripian/react";
import Model from "@tripian/model";
import ICombinedState from "../../redux/model/ICombinedState";

import {
  TIMELINE_PLAN,
  LOGIN,
  USER_PROFILE,
  TRIPS,
  EDIT_TIMELINE_TRIP,
} from "../../constants/ROUTER_PATH_TITLE";
import TimelineMapLayout from "./components/TimelineMapLayout";
import Menu from "../TripPlan/layouts/Menu/Menu";
import TimelinePlanContainer from "./components/TimelinePlanContainer";
import FocusContainer from "../TripPlan/layouts/TripLayout/Container/FocusContainer/FocusContainer";
import TimelineSearchContainer from "./components/TimelineSearchContainer/TimelineSearchContainer";
import TimelineFavoritesContainer from "./components/TimelineFavoritesContainer/TimelineFavoritesContainer";
import MyBookingsContainer from "../TripPlan/layouts/TripLayout/Container/MyBookingsContainer/MyBookingsContainer";

import useAuth from "../../hooks/useAuth";
import useTimeline from "../../hooks/useTimeline";
import useAlternative from "../../hooks/useAlternative";
import usePoiCategory from "../../hooks/usePoiCategory";
import useFocus from "../../hooks/useFocus";
import useLayoutPlan from "../TripPlan/hooks/useLayoutPlan";
import AppNav from "../../App/AppNav/AppNav";
import TripAppMenu from "../../App/AppNav/TripMenu/TripAppMenu";

import useTranslate from "../../hooks/useTranslate";
import useTravelify from "../../hooks/useTravelify";
import TripLayout from "../TripPlan/layouts/TripLayout/TripLayout";
import BookableExperiencesContainer from "../TripPlan/layouts/TripLayout/Container/BookableExperiencesContainer/BookableExperiencesContainer";
import BookableExperiencesContainerLegacy from "../TripPlan/layouts/TripLayout/Container/BookableExperiencesContainer/BookableExperiencesContainerLegacy";
import TravelifyContainer from "../../components/TravelifyContainer/TravelifyContainer";
import isTourApiEnabled from "../../utils/isTourApiEnabled";
import VictoryProductInfo from "../TourInfo/VictoryProductInfo/VictoryProductInfo";
import VistorTourInfo from "../TourInfo/VistorTourInfo/VistorTourInfo";
import GygTourInfo from "../TourInfo/GygTourInfo/GygTourInfo";
import ToristyTourInfo from "../TourInfo/ToristyTourInfo/ToristyTourInfo";
import LivnTourInfo from "../TourInfo/LivnTourInfo/LivnTourInfo";
import { useGuestUser } from "../../hooks/useGuestUser";
import {
  adaptTimelineTripToReference,
  getPlansFromTimelineDays,
  groupTimelinePlansBySegment,
  TimelineSegmentGroup,
} from "../../utils/timelineAdapters";
import { LoginModal } from "../../components/LoginModal/LoginModal";
import { CloneModal } from "../../components/CloneModal/CloneModal";
import ShareTripModal from "../../components/ShareTripModal/ShareTripModal";

import classes from "../TripPlan/TripPlan.module.scss";
import TimelineTripLayout from "./layouts/TripLayout/TimelineTripLayout";
import GuestShareHeader from "../../components/ShareTripModal/GuestShareHeader";
import * as gtag from "../../gtag";
const mapClasses = ["map-container"];

/**
 * Detayı uygulama içinde modal olarak açılan provider'lar. Aşağıda bu provider'lar
 * için bir TourInfo bileşeni render ediliyor; listeye provider eklerken modalını da
 * eklemek gerekiyor. ToursandTickets sayfasındaki OVERLAY_PROVIDER_IDS ile aynı liste.
 */
const MODAL_PROVIDER_IDS: Model.PROVIDER_ID[] = [
  Model.PROVIDER_ID.GYG,
  Model.PROVIDER_ID.VIATOR,
  Model.PROVIDER_ID.TORISTY,
  Model.PROVIDER_ID.VICTORY,
  Model.PROVIDER_ID.LIVN,
];


// Referansı stabil boş listeler: her render'da yeni array üretip memo'lu kartları boşuna yenilemesin
const EMPTY_TOUR_IDS = {
  gyg: [] as number[],
  viator: [] as string[],
  toristy: [] as string[],
  bb: [] as number[],
};

const TimelinePlanPage = () => {
  const { isLoggedIn } = useAuth();
  const {
    loadingTimeline,
    timelineTrip,
    timelineDays,
    timelineReadOnly,
    timelineFetch,
    timelineGetShared,
    timelineShare,
    timelineSegmentDelete,
    stopPolling,
  } = useTimeline();

  // Get plans from Redux - this updates when useTimelineStep refetches
  const reduxPlans = useSelector((state: ICombinedState) => state.trip.plans);

  const { alternatives, alternativesFetch } = useAlternative();
  const { poiCategoryGroups, loadingPoiCategories } = usePoiCategory();
  const [selectedPoiCategoryIds, setSelectedPoiCategoryIds] = useState<number[]>([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const { focusLost, focus } = useFocus();
  const [tripListVisible, setTripListVisible] = useState<boolean>(true);
  const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);

  const {
    favoritesVisible,
    setFavoritesVisible,
    yourBookingsVisible,
    setYourBookingsVisible,
    offersMyVisible,
    setOffersMyVisible,
    offersSearchVisible,
    setOffersSearchVisible,
  } = useLayoutPlan();

  const [isBookingClicked, setIsBookingClicked] = useState<boolean>(false);
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const [showShareTripModal, setShowShareTripModal] = useState<boolean>(false);
  const [shareTripModalLoading, setShareTripModalLoading] = useState<boolean>(false);
  // Active segment index for map - show route only for this segment
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number | null>(0);

  const { t } = useTranslate();
  const navigate = useNavigate();

  const { hashParam, dayIndex } = useParams<{
    hashParam?: string;
    dayIndex?: string;
  }>();

  const dayIndexNumber = useMemo(() => {
    const index = Number(dayIndex);
    return isNaN(index) ? 0 : index;
  }, [dayIndex]);

  // Parse hash from hashParam
  const { hash, isShared } = useMemo(() => {
    if (!hashParam) return { hash: "", isShared: false };
    const params = hashParam.split("!");
    return {
      hash: params[0],
      isShared: params.length > 1 && params[1] === "s",
    };
  }, [hashParam]);

  const isGuest = useGuestUser();

  // Adapt timeline data for existing components
  const tripReference = useMemo(() => {
    if (!timelineTrip) return undefined;
    return adaptTimelineTripToReference(timelineTrip);
  }, [timelineTrip]);

  const plans = useMemo(() => {
    if (!timelineDays || timelineDays.length === 0) return undefined;
    const basePlans = getPlansFromTimelineDays(timelineDays);
    // Use steps from reduxPlans if available (more up-to-date after step operations)
    if (reduxPlans && reduxPlans.length === basePlans.length) {
      return basePlans.map((plan, index) => ({
        ...plan,
        steps: reduxPlans[index]?.steps || plan.steps,
      }));
    }
    return basePlans;
  }, [timelineDays, reduxPlans]);

  // Compute segmentGroups from Redux plans - this ensures UI updates when steps change
  // reduxPlans is updated by useTimelineStep after step operations (add/delete/replace)
  // Use reduxPlans steps for real-time UI updates, timelineTrip.plans for metadata (name, dates, etc.)
  const segmentGroups = useMemo<TimelineSegmentGroup[]>(() => {
    if (!timelineTrip?.plans || timelineTrip?.plans.length === 0 || !timelineTrip?.city) return [];
    const timelinePlans: Model.TimelinePlan[] = timelineTrip.plans.map((plan, index) => {
      // Use steps from reduxPlans if available (more up-to-date after step operations)
      const reduxPlan = reduxPlans?.[index];
      return {
        id: plan.id.toString(),
        name: plan.name || `Day ${index + 1}`,
        description: "",
        available: true,
        generatedStatus: plan.generatedStatus,
        startDate: plan.startDate?.includes(" ") ? plan.startDate.replace(" ", "T") + ":00" : `${plan.startDate}T09:00:00`,
        endDate: plan.endDate?.includes(" ") ? plan.endDate.replace(" ", "T") + ":00" : `${plan.endDate}T21:00:00`,
        steps: reduxPlan?.steps || plan.steps,
        city: timelineTrip.city,
        tripType: 0,
        children: timelineTrip?.tripProfile?.children || 0,
        pets: 0,
        adults: timelineTrip?.tripProfile?.adults || 1,
      };
    });

    return groupTimelinePlansBySegment(timelinePlans, timelineTrip?.tripProfile?.segments);
  }, [timelineTrip, reduxPlans]);

  const isFocusOpen = !!focus?.step || !!focus?.poi || !!focus.providerTour;


    const tripUrl = useMemo(() => {
        if (!tripReference) return "";
        return `${window.location.origin}/timeline/${tripReference.tripHash}!s`;
    }, [tripReference, dayIndexNumber]);

  // Track if we've already fetched for this hash
  const fetchedHashRef = useRef<string | null>(null);
  // Track if auto-share has been triggered
  const autoShareTriggeredRef = useRef<boolean>(false);

  // Fetch timeline on mount
  useEffect(() => {
    if (!hash) return;
    // Skip if already fetched for this hash
    if (fetchedHashRef.current === hash) return;

    fetchedHashRef.current = hash;

    if (isShared) {
      timelineGetShared(hash, dayIndexNumber);
    } else if (isLoggedIn || isGuest) {
      timelineFetch(hash, dayIndexNumber);
    }
  }, [hash, isShared, isLoggedIn, isGuest, dayIndexNumber, timelineFetch, timelineGetShared]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoggedIn && !isShared && !isGuest) {
      navigate(LOGIN.PATH, {
        replace: true,
        state: { fromTimelinePlan: true, tripHash: hashParam },
      });
    }
  }, [isLoggedIn, isShared, isGuest, navigate, hashParam]);

  // Escape key handler
  useEffect(() => {
    if (!isFocusOpen || imageModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        focusLost();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFocusOpen, imageModalOpen, focusLost]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      focusLost();
      stopPolling();
    };
  }, [focusLost, stopPolling]);

  // Auto-share timeline if not already shared (only for non-shared URLs)
  useEffect(() => {
    if (!isShared && timelineTrip && timelineTrip.shared === false && hash && !autoShareTriggeredRef.current) {
      autoShareTriggeredRef.current = true;
      timelineShare(hash, true);
    }
  }, [isShared, timelineTrip, hash, timelineShare]);

  // Alternatives - fetch for ALL days in timeline to support all segments
  useEffect(() => {
    if (plans && plans.length > 0) {
      // Fetch alternatives for each day to ensure all segments have their alternatives
      plans.forEach((_, index) => {
        alternativesFetch(index);
      });
    }
  }, [alternativesFetch, plans]);

  // useAllTourIds şehir katalogunun tamamını sayfa sayfa indiriyordu (arkaplanda
  // yüzlerce tour search isteği); Book rozeti artık backend'in poi.hasBookings
  // flag'inden geldiği ve bu listeler hiçbir tüketicide okunmadığı için boş sabit geçiliyor.
  const gygTourIds = EMPTY_TOUR_IDS.gyg;
  const viatorTourIds = EMPTY_TOUR_IDS.viator;
  const toristyTourIds = EMPTY_TOUR_IDS.toristy;
  const bbTourIds = EMPTY_TOUR_IDS.bb;
  const toursLoading = false;

  const { travelifyUrl, loadingTravelify } = useTravelify(
    tripReference?.city.coordinate.lat,
    tripReference?.city.coordinate.lng,
    tripReference?.tripProfile.arrivalDatetime,
    tripReference?.tripProfile.departureDatetime,
    tripReference?.tripProfile.numberOfAdults,
    Number(tripReference?.tripProfile.numberOfChildren)
  );


  // Set document title
  document.title = `${TIMELINE_PLAN.TITLE(t("tripPlan.header"))} - ${tripReference?.city.name || ""} - ${hash}`;

  // Alternative step Id
  const [alternativesStepId, setAlternativesStepId] = useState<number | undefined>(undefined);

  useEffect(() => {
    setAlternativesStepId(undefined);
  }, [dayIndexNumber]);

  useEffect(() => {
    if (isFocusOpen) {
      setIsBookingClicked(false);
    }
  }, [isFocusOpen]);

  const closeAllContainers = () => {
    setFavoritesVisible(false);
    setYourBookingsVisible(false);
    setOffersMyVisible(false);
    setOffersSearchVisible(false);
  };

  const openContainer = (containerSetter: (value: boolean) => void) => {
    closeAllContainers();
    containerSetter(true);
  };

  // Share trip toggle handler
  const switchCheckedOnchange = (checked: boolean) => {
    if (tripReference && hash) {
      setShareTripModalLoading(true);
      timelineShare(hash, checked).finally(() => {
        setShareTripModalLoading(false);
      });
    }
  };

  // Tour modal state
  const [showTourModal, setShowTourModal] = useState(false);
  const [selectedTour, setSelectedTour] = useState<{
    provider: Model.PROVIDER_ID;
    tourId: string;
    cityId?: number;
    cityName?: string;
    startDate?: string;
    endDate?: string;
    adults?: number;
    children?: number;
  } | null>(null);

  // Handle back button for non-shared trips
  useEffect(() => {
    if (isShared || isGuest) return;

    const handlePopState = () => {
      if (showTourModal) {
        setShowTourModal(false);
      } else {
        navigate(TRIPS.PATH, { replace: true });
      }
    };

    const currentState = window.history.state;
    const newState = { ...currentState, fromCustomPage: true };
    window.history.pushState(newState, "", window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [showTourModal, isShared, isGuest, navigate]);

  const handleTourClick = (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => {
    if (!window.tconfig.TOUR_TICKET_PROVIDER_IDS.includes(provider)) return;

    gtag.googleAnalyticsEvent("tour_modal_open", { provider, tour_id: tourId });

    // Modalı olmayan her provider ürünün kendi sayfasına gider. Bu ayrım allowlist
    // olmak zorunda: denylist tutulduğunda listeye eklenmeyen provider modal state'ine
    // düşüyor, aşağıda o provider için render edilen bir modal olmadığı için de
    // tıklama sessizce kayboluyordu (OccasionGenius'ta yaşanan buydu).
    // Victory Live tourApi'den kendi url'iyle gelir; modalı ise doğrudan Victory API'sini
    // (providers.victory) ister ve o çoğu sitede başlatılmıyor. Url varsa ürün sayfasını aç.
    const victoryWithUrl =
      provider === Model.PROVIDER_ID.VICTORY && !!additionalData?.url;
    if (!MODAL_PROVIDER_IDS.includes(provider) || victoryWithUrl) {
      // Rezdy'de ürün url'i yoksa booking widget'ı productCode'dan kurulabiliyor
      // (FocusContainer ve RezdyTourInfo da aynı url'i üretiyor).
      const externalUrl =
        additionalData?.url ||
        (provider === Model.PROVIDER_ID.REZDY
          ? `https://365adventures.rezdy.com/calendarWidget/${tourId}`
          : undefined);
      if (externalUrl) window.open(externalUrl, "_blank");
      return;
    }

    // Trip bağlamı (şehir, tarihler, yolcu sayısı) her modal provider'a gitmeli:
    // Viator traveler sayılarını, Livn tarihleri bundan dolduruyor. Çağıran taraf
    // kendi değerlerini gönderirse (FocusContainer) onlar öncelikli.
    const selected = {
      provider,
      tourId,
      cityId: tripReference?.city.id,
      cityName: tripReference?.city.name,
      startDate: tripReference?.tripProfile.arrivalDatetime,
      endDate: tripReference?.tripProfile.departureDatetime,
      adults: tripReference?.tripProfile.numberOfAdults,
      children: tripReference?.tripProfile.numberOfChildren || undefined,
      ...additionalData,
    };
    setSelectedTour({ ...selected, children: selected.children || undefined });
    setShowTourModal(true);
  };

  // Video modal
  const videoModal = useMemo(() => {
    if (focus.providerVideo) {
      return (
        <Modal
          className="!w-[90%] !max-w-[100vw] bg-background-color md:!w-[70%] md:!max-w-[70vw] max-h-[90vh] overflow-auto"
          show={!!focus.providerVideo}
          backdropClick={() => focusLost()}
          zIndex={499}
        >
          <div className="p-4">
            <div>
              <CloseIconButton2
                fill="#2B2B33"
                clicked={() => focusLost()}
                rounded
              />
            </div>
            <div>
              <div className="flex items-center justify-center font-bold text-xl mr-1">
                {focus.providerVideo?.name}
              </div>
              <div className="mt-4">
                <video
                  controls
                  autoPlay
                  muted
                  className="w-full h-auto max-h-[70vh] object-contain"
                  src={`https://stream.mux.com/${focus.providerVideo.mux_playbackid}/capped-1080p.mp4`}
                />
              </div>
            </div>
          </div>
        </Modal>
      );
    }
    return null;
  }, [focus.providerVideo, focusLost]);
  const isValaiLanding = window.tconfig.LANDING_PAGE === "ValaiLanding";

  if (!tripListVisible) mapClasses.push("w-m-100");

  return (
    <>
      <AppNav
        tripInfoHeader={false}
        tripAppMenu={
          <TripAppMenu
            onClickBookings={() => {
              focusLost();
              openContainer(setYourBookingsVisible);
              setShowTourModal(false);
            }}
            onClickOffers={() => {
              focusLost();
              openContainer(setOffersMyVisible);
              setShowTourModal(false);
            }}
            onClickFavorites={() => {
              focusLost();
              openContainer(setFavoritesVisible);
              setShowTourModal(false);
            }}
            isInTripPlan={tripReference !== undefined}
            sharedTrip={isShared}
            t={t}
          />
        }
        centerContent={
          isValaiLanding ? (
                <GuestShareHeader
                    tripUrl={tripUrl}
                    onMountShare={() => {
                        if (tripReference && tripReference.shared === false) {
                            switchCheckedOnchange(true);
                        }
                    }}
                    t={t}
                />
            ) : undefined
        }
        sharedTrip={isShared}
      />

      {loadingTimeline || loadingPoiCategories ? <PageLoading /> : null}

      {tripReference && plans && (
        <div className={classes.container}>
          <div className={tripListVisible ? "hide-s" : ""}>
            <TimelineMapLayout
              tripReference={tripReference}
              segmentGroups={segmentGroups}
              activeSegmentIndex={activeSegmentIndex}
              fullWidth={!tripListVisible}
              alternativesStepId={alternativesStepId}
              poiCategoryGroups={poiCategoryGroups}
              offersSearchVisible={offersSearchVisible}
              setOffersSearchVisible={setOffersSearchVisible}
              loadingSearchOffers={false}
              offersResult={[]}
              searchOffer={() => Promise.resolve([])}
              sharedTrip={isShared}
              selectedPoiCategoryIds={selectedPoiCategoryIds}
              setSelectedPoiCategoryIds={setSelectedPoiCategoryIds}
              eventCardClicked={(eventid: number) =>
                handleTourClick(Model.PROVIDER_ID.VICTORY, eventid.toString())
              }
              setShowCloneModal={() => setShowCloneModal(true)}
              onAddStorageToItinerary={() => {}}
              t={t}
            />
          </div>
          <div className={!tripListVisible ? "hide-s" : ""}>
            <TimelineTripLayout
              tripReference={tripReference}
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={setSelectedTabIndex}
              setShowCloneModal={() => setShowCloneModal(true)}
              setShowShareTripModal={() => setShowShareTripModal(true)}
              isFocusOpen={isFocusOpen}
              onFocusLost={focusLost}
              editPath={`${EDIT_TIMELINE_TRIP.PATH}/${hash}`}
              t={t}
            >
              {segmentGroups && segmentGroups.length > 0 ? (
                <TimelinePlanContainer
                  show={selectedTabIndex === 0}
                  alternativesStepId={alternativesStepId}
                  setAlternativesStepId={setAlternativesStepId}
                  gygTourIds={gygTourIds}
                  bbTourIds={bbTourIds}
                  viatorTourIds={viatorTourIds}
                  toristyTourIds={toristyTourIds}
                  toursLoading={toursLoading}
                  setShowCloneModal={() => setShowCloneModal(true)}
                  onBookingClick={() => setIsBookingClicked(true)}
                  segmentGroups={segmentGroups}
                  activeSegmentIndex={activeSegmentIndex}
                  setActiveSegmentIndex={setActiveSegmentIndex}
                  onSegmentDelete={(segmentIndex) => timelineSegmentDelete(hash, segmentIndex)}
                />
              ) : null}

              {tripReference && plans ? (
                <TimelineSearchContainer
                  show={selectedTabIndex === 2}
                  dayIndex={dayIndexNumber}
                  tripHash={tripReference.tripHash}
                  tripReadOnly={timelineReadOnly}
                  plans={plans}
                  alternatives={alternatives || []}
                  poiCategoryGroups={poiCategoryGroups}
                  gygTourIds={gygTourIds}
                  bbTourIds={bbTourIds}
                  viatorTourIds={viatorTourIds}
                  toristyTourIds={toristyTourIds}
                  toursLoading={toursLoading}
                  onBookingClick={() => setIsBookingClicked(true)}
                  t={t}
                />
              ) : null}

              {travelifyUrl ? (
                <TravelifyContainer
                  show={selectedTabIndex === 1}
                  travelifyUrl={travelifyUrl}
                  isLoading={selectedTabIndex === 1 && loadingTravelify}
                  t={t}
                />
              ) : isTourApiEnabled() ? (
                <BookableExperiencesContainer
                  show={selectedTabIndex === 1}
                  tripReference={tripReference}
                  t={t}
                  onTourClick={handleTourClick}
                />
              ) : (
                <BookableExperiencesContainerLegacy
                  show={selectedTabIndex === 1}
                  tripReference={tripReference}
                  t={t}
                  onTourClick={handleTourClick}
                />
              )}
            </TimelineTripLayout>
          </div>
        </div>
      )}

      {plans && (
        <FocusContainer
          isBookingClicked={isBookingClicked}
          planId={plans[dayIndexNumber]?.id || 0}
          isLoadingOffer={() => false}
          myAllOffers={[]}
          offerOptIn={() => Promise.resolve()}
          offerOptOut={() => Promise.resolve()}
          hideActionButtons={isShared && window.tconfig.WIDGET_THEME_1}
          hideFavoriteIcon={isShared && window.tconfig.WIDGET_THEME_1}
          hidePartOfDay={isShared && window.tconfig.WIDGET_THEME_1}
          hideFeatures={isShared && window.tconfig.WIDGET_THEME_1}
          hideCuisine={isShared && window.tconfig.WIDGET_THEME_1}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          timezone={tripReference?.city.timezone}
          onImageModalChange={(isOpen: boolean) => setImageModalOpen(isOpen)}
          onTourClick={handleTourClick}
        />
      )}

      {tripReference && plans ? (
        <TimelineFavoritesContainer
          show={favoritesVisible}
          dayIndex={dayIndexNumber}
          tripHash={tripReference.tripHash}
          tripReadOnly={timelineReadOnly}
          plans={plans}
          isFocusOpen={isFocusOpen}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          close={() => setFavoritesVisible(false)}
          onBookingClick={() => setIsBookingClicked(true)}
        />
      ) : null}

      <MyBookingsContainer
        show={yourBookingsVisible}
        close={() => setYourBookingsVisible(false)}
      />

      {isFocusOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-10 z-50"
          onClick={focusLost}
          aria-label="Close focus panel"
        />
      )}

      <Menu
        listShown={tripListVisible}
        sharedTrip={isShared}
        isInTripPlan={tripReference !== undefined}
        showList={() => setTripListVisible(!tripListVisible)}
        showFavorites={() => {
          focusLost();
          openContainer(setFavoritesVisible);
        }}
        showOffers={() => openContainer(setOffersMyVisible)}
        showBookings={() => openContainer(setYourBookingsVisible)}
        showProfile={() => navigate(USER_PROFILE.PATH)}
        t={t}
      />

      {videoModal}

      {/* Tour Modals */}
      {selectedTour && selectedTour.provider === Model.PROVIDER_ID.VICTORY && showTourModal && (
        <VictoryProductInfo
          productId={selectedTour.tourId}
          isOpen={showTourModal && selectedTour.provider === Model.PROVIDER_ID.VICTORY}
          onClose={() => setShowTourModal(false)}
        />
      )}
      {selectedTour && selectedTour.provider === Model.PROVIDER_ID.TORISTY && showTourModal && (
        <ToristyTourInfo
          tourId={selectedTour.tourId}
          isOpen={true}
          onClose={() => {
            setShowTourModal(false);
            setSelectedTour(null);
          }}
        />
      )}
      {selectedTour && selectedTour.provider === Model.PROVIDER_ID.VIATOR && showTourModal && (
        <VistorTourInfo
          tourId={selectedTour.tourId}
          cityId={selectedTour.cityId || 0}
          cityName={selectedTour.cityName}
          startDate={selectedTour.startDate}
          endDate={selectedTour.endDate}
          adults={selectedTour.adults}
          children={selectedTour.children}
          previousRoute={`${TIMELINE_PLAN.PATH}/${hashParam}`}
          isOpen={showTourModal && selectedTour.provider === Model.PROVIDER_ID.VIATOR}
          onClose={() => setShowTourModal(false)}
        />
      )}
      {selectedTour && selectedTour.provider === Model.PROVIDER_ID.LIVN && showTourModal && (
        <LivnTourInfo
          tourId={Number(selectedTour.tourId)}
          startDate={selectedTour.startDate}
          endDate={selectedTour.endDate}
          isOpen
          onClose={() => {
            setShowTourModal(false);
            setSelectedTour(null);
          }}
        />
      )}
      {selectedTour && selectedTour.provider === Model.PROVIDER_ID.GYG && showTourModal && (
        <GygTourInfo
          tourId={Number(selectedTour.tourId)}
          cityId={selectedTour.cityId || 0}
          cityName={selectedTour.cityName}
          startDate={selectedTour.startDate}
          endDate={selectedTour.endDate}
          adults={selectedTour.adults}
          children={selectedTour.children}
          isOpen={showTourModal && selectedTour.provider === Model.PROVIDER_ID.GYG}
          onClose={() => setShowTourModal(false)}
          t={t}
        />
      )}

      {/* Login & Clone Modals for shared trips */}
      {showLoginModal && (
        <LoginModal
          sharedTripHash={tripReference?.tripHash ?? ""}
          onCancel={() => setShowLoginModal(false)}
        />
      )}
      {showCloneModal && (
        <CloneModal
          sharedTripHash={tripReference?.tripHash ?? ""}
          onCancel={() => setShowCloneModal(false)}
          isTimeline={true}
        />
      )}

      {/* Share Trip Modal */}
      {showShareTripModal && tripReference && (
        <ShareTripModal
          onCancel={() => setShowShareTripModal(false)}
          loading={shareTripModalLoading}
          switchChecked={tripReference.shared}
          onChange={switchCheckedOnchange}
          cityName={tripReference.city.name}
          arrivalDatetime={tripReference.tripProfile.arrivalDatetime}
          departureDatetime={tripReference.tripProfile.departureDatetime}
          tripHash={tripReference.tripHash}
          dayIndex={dayIndexNumber}
        />
      )}
    </>
  );
};

export default TimelinePlanPage;
